/**
 * Catálogo de permissões de negócio para a tela de perfis (espelha fv-api/RBAC.md, seção 2).
 *
 * A API (`GET /profiles/permissions`) devolve cada permissão com `Key` (<recurso>.<ação>),
 * `ID` (usado para salvar o perfil), `DisplayName` e `Group` (legados). A tela agrupa por
 * ÁREA (abaixo), mostra o rótulo em português de cada ação e marca as sensíveis.
 * Chave que a API devolver e que não esteja aqui cai na área "Outras" com o `DisplayName`.
 */

// `module`: módulo do plano (planmodule.Catalog) que precisa estar contratado para a área
// aparecer. Só os módulos já gateados em usePermissions (getModuleForPermission).
export const AREAS = [
    { key: 'home', label: 'Home', description: 'Indicadores (KPIs) da tela inicial', keys: ['home.view'] },
    {
        key: 'sales', label: 'Vendas e PDV', description: 'Frente de caixa, histórico e ações sensíveis de venda', module: 'sale',
        keys: ['sale.view', 'sale.create', 'sale.item.update', 'sale.item.cancel', 'sale.generic.rename', 'sale.discount.item', 'sale.discount.sale', 'sale.cancel', 'sale.reverse'],
    },
    {
        key: 'serviceorders', label: 'Ordens de serviço', description: 'Abertura, execução, faturamento e envio de ordens',
        keys: ['serviceorder.view', 'serviceorder.create', 'serviceorder.update', 'serviceorder.execute', 'serviceorder.checklist.fill', 'serviceorder.bill', 'serviceorder.send', 'serviceorder.upcoming', 'serviceorder.cancel', 'serviceorder.delete'],
    },
    {
        key: 'servicequotes', label: 'Orçamentos', description: 'Geração, envio e acompanhamento de orçamentos', module: 'quote',
        keys: ['servicequote.view', 'servicequote.create', 'servicequote.update', 'servicequote.send', 'servicequote.delete'],
    },
    { key: 'customers', label: 'Clientes', description: 'Cadastro de clientes', keys: ['customer.view', 'customer.details', 'customer.create', 'customer.update', 'customer.import', 'customer.export', 'customer.delete'] },
    { key: 'suppliers', label: 'Fornecedores', description: 'Cadastro de fornecedores', keys: ['supplier.view', 'supplier.create', 'supplier.update', 'supplier.delete'] },
    { key: 'vehicles', label: 'Veículos', description: 'Veículos dos clientes', module: 'vehicle', keys: ['vehicle.view', 'vehicle.create', 'vehicle.update', 'vehicle.export', 'vehicle.delete'] },
    {
        key: 'checklist', label: 'Checklists', description: 'Modelos, grupos de inspeção e preenchimento',
        keys: ['checklist.view', 'checklist.create', 'checklist.update', 'checklist.delete', 'checklist.group.view', 'checklist.group.create', 'checklist.group.update', 'checklist.group.delete'],
    },
    {
        key: 'products', label: 'Produtos e estoque', description: 'Catálogo de produtos, reposição, importação e exportação', module: 'product',
        keys: ['product.view', 'product.create', 'product.update', 'product.restock', 'product.import', 'product.export', 'product.delete'],
    },
    { key: 'purchases', label: 'Compras', description: 'Importação de notas fiscais e entrada no estoque', module: 'product', keys: ['purchase.view', 'purchase.create', 'purchase.cancel'] },
    { key: 'categories', label: 'Categorias', description: 'Categorias de produtos e serviços', module: 'product', keys: ['category.view', 'category.create', 'category.update', 'category.delete'] },
    { key: 'inventory', label: 'Inventário', description: 'Contagem, conferência e aprovação do inventário', module: 'inventory', keys: ['inventory.view', 'inventory.create', 'inventory.update', 'inventory.approve'] },
    { key: 'services', label: 'Serviços', description: 'Catálogo de serviços oferecidos', keys: ['service.view', 'service.create', 'service.update', 'service.import', 'service.export', 'service.delete'] },
    {
        key: 'cashregister', label: 'Caixa', description: 'Abertura, fechamento, sangria e histórico', module: 'financial',
        keys: ['cashregister.view', 'cashregister.history', 'cashregister.create', 'cashregister.update', 'cashregister.open', 'cashregister.close', 'cashregister.bleed', 'cashregister.delete'],
    },
    { key: 'cardmachines', label: 'Maquininhas', description: 'Cadastro de maquininhas de cartão (a venda lista as maquininhas sem esta permissão)', module: 'financial', keys: ['cardmachine.view', 'cardmachine.create', 'cardmachine.update', 'cardmachine.delete'] },
    {
        key: 'accounts', label: 'Contas a pagar e a receber', description: 'Lançamentos, contas a pagar e contas a receber', module: 'financial',
        keys: ['accounts.payable.view', 'accounts.payable.create', 'accounts.payable.update', 'accounts.receivable.view', 'accounts.receivable.create', 'accounts.receivable.update'],
    },
    {
        key: 'reports', label: 'Relatórios e dashboards', description: 'Relatórios, fluxo de caixa e painéis de indicadores',
        keys: ['report.sale', 'report.product', 'report.product.updatecost', 'report.product.pricing', 'report.stock', 'report.customer', 'report.serviceorder', 'report.vehicle', 'report.checklist', 'report.inventory', 'report.cashflow', 'dashboard.sale', 'dashboard.serviceorder', 'dashboard.servicequote', 'dashboard.vehicle', 'dashboard.customer', 'dashboard.product'],
    },
    { key: 'users', label: 'Usuários e perfis', description: 'Contas de acesso e perfis de permissão', keys: ['user.view', 'user.create', 'user.update', 'user.delete', 'profile.view', 'profile.create', 'profile.update', 'profile.delete'] },
    // Configurações: UMA área com 4 sub-seções (a sessão de cada tela /settings). `view` mostra a
    // sessão em somente leitura; `update` permite alterar. Notificações e fidelidade (grupo
    // legado `general`, sem tela própria) ficam na sub-seção Gerais.
    {
        key: 'settings', label: 'Configurações', description: 'Sessões da tela de configurações: Ordem de Serviço, Vendas, Gerais e Integrações',
        keys: [
            'settings.serviceorder.view', 'settings.serviceorder.update',
            'settings.sale.view', 'settings.sale.update',
            'settings.general.view', 'settings.general.update', 'notification.view', 'notification.delete', 'loyalty.config',
            'settings.integration.view', 'settings.integration.update',
        ],
    },
    { key: 'audit', label: 'Auditoria', description: 'Consulta ao log de auditoria', module: 'audit', keys: ['audit.view'] },
    { key: 'ai', label: 'Assistente de IA', description: 'Uso do assistente de IA', module: 'ai_assistant', keys: ['ai.use'] },
];

// Sub-seção (dentro da área) por chave; só a área Configurações usa.
export const SECTION_BY_KEY = {
    'settings.serviceorder.view': 'Ordem de Serviço', 'settings.serviceorder.update': 'Ordem de Serviço',
    'settings.sale.view': 'Vendas', 'settings.sale.update': 'Vendas',
    'settings.general.view': 'Gerais', 'settings.general.update': 'Gerais',
    'notification.view': 'Gerais', 'notification.delete': 'Gerais', 'loyalty.config': 'Gerais',
    'settings.integration.view': 'Integrações', 'settings.integration.update': 'Integrações',
};

export const OTHER_AREA = { key: 'other', label: 'Outras', description: 'Permissões ainda sem rótulo nesta tela', keys: [] };

// Ação legível por chave. `s: true` marca ação sensível (destaque visual).
export const ACTIONS = {
    'home.view': { label: 'Ver indicadores' },
    'sale.view': { label: 'Consultar vendas' },
    'sale.create': { label: 'Vender (PDV)' },
    'sale.item.update': { label: 'Atualizar item' },
    'sale.item.cancel': { label: 'Cancelar item', s: true },
    'sale.generic.rename': { label: 'Renomear produto genérico' },
    'sale.discount.item': { label: 'Desconto no item', s: true },
    'sale.discount.sale': { label: 'Desconto na venda', s: true },
    'sale.cancel': { label: 'Cancelar venda', s: true },
    'sale.reverse': { label: 'Estornar venda', s: true },
    'serviceorder.view': { label: 'Listar' },
    'serviceorder.create': { label: 'Gerar' },
    'serviceorder.update': { label: 'Editar' },
    'serviceorder.execute': { label: 'Executar' },
    'serviceorder.checklist.fill': { label: 'Preencher checklist' },
    'serviceorder.bill': { label: 'Faturar (receber)' },
    'serviceorder.send': { label: 'Enviar por WhatsApp' },
    'serviceorder.upcoming': { label: 'Próximos serviços' },
    'serviceorder.cancel': { label: 'Cancelar', s: true },
    'serviceorder.delete': { label: 'Remover', s: true },
    'servicequote.view': { label: 'Listar' },
    'servicequote.create': { label: 'Incluir' },
    'servicequote.update': { label: 'Editar' },
    'servicequote.send': { label: 'Enviar ao cliente' },
    'servicequote.delete': { label: 'Remover', s: true },
    'customer.view': { label: 'Listar' },
    'customer.details': { label: 'Ver ficha do cliente' },
    'customer.create': { label: 'Incluir' },
    'customer.update': { label: 'Editar' },
    'customer.import': { label: 'Importar' },
    'customer.export': { label: 'Exportar' },
    'customer.delete': { label: 'Remover', s: true },
    'supplier.view': { label: 'Listar' },
    'supplier.create': { label: 'Incluir' },
    'supplier.update': { label: 'Editar' },
    'supplier.delete': { label: 'Remover', s: true },
    'vehicle.view': { label: 'Listar' },
    'vehicle.export': { label: 'Exportar' },
    'vehicle.create': { label: 'Incluir' },
    'vehicle.update': { label: 'Editar' },
    'vehicle.delete': { label: 'Remover', s: true },
    'checklist.view': { label: 'Listar' },
    'checklist.create': { label: 'Incluir' },
    'checklist.update': { label: 'Editar' },
    'checklist.delete': { label: 'Remover', s: true },
    'checklist.group.view': { label: 'Grupos: listar' },
    'checklist.group.create': { label: 'Grupos: incluir' },
    'checklist.group.update': { label: 'Grupos: editar' },
    'checklist.group.delete': { label: 'Grupos: remover', s: true },
    'product.view': { label: 'Listar' },
    'product.create': { label: 'Incluir' },
    'product.update': { label: 'Editar' },
    'product.restock': { label: 'Repor estoque' },
    'product.import': { label: 'Importar' },
    'product.export': { label: 'Exportar' },
    'product.delete': { label: 'Remover', s: true },
    'purchase.view': { label: 'Listar compras' },
    'purchase.create': { label: 'Lançar compra' },
    'purchase.cancel': { label: 'Cancelar compra', s: true },
    'category.view': { label: 'Listar' },
    'category.create': { label: 'Incluir' },
    'category.update': { label: 'Editar' },
    'category.delete': { label: 'Remover', s: true },
    'inventory.view': { label: 'Listar' },
    'inventory.create': { label: 'Incluir' },
    'inventory.update': { label: 'Contar e editar' },
    'inventory.approve': { label: 'Aprovar e ajustar estoque', s: true },
    'service.view': { label: 'Listar' },
    'service.create': { label: 'Incluir' },
    'service.update': { label: 'Editar' },
    'service.import': { label: 'Importar' },
    'service.export': { label: 'Exportar' },
    'service.delete': { label: 'Remover', s: true },
    'cashregister.view': { label: 'Visualizar' },
    'cashregister.history': { label: 'Histórico' },
    'cashregister.create': { label: 'Incluir' },
    'cashregister.update': { label: 'Editar' },
    'cashregister.open': { label: 'Abrir' },
    'cashregister.close': { label: 'Fechar' },
    'cashregister.bleed': { label: 'Sangria', s: true },
    'cashregister.delete': { label: 'Remover', s: true },
    'cardmachine.view': { label: 'Listar' },
    'cardmachine.create': { label: 'Incluir' },
    'cardmachine.update': { label: 'Editar' },
    'cardmachine.delete': { label: 'Remover', s: true },
    'accounts.payable.view': { label: 'A pagar: listar' },
    'accounts.payable.create': { label: 'A pagar: incluir' },
    'accounts.payable.update': { label: 'A pagar: editar e baixar' },
    'accounts.receivable.view': { label: 'A receber: listar' },
    'accounts.receivable.create': { label: 'A receber: incluir' },
    'accounts.receivable.update': { label: 'A receber: editar e baixar' },
    'report.sale': { label: 'Relatório de vendas' },
    'report.product': { label: 'Relatório de produtos' },
    'report.product.updatecost': { label: 'Alterar custo do produto na venda', s: true },
    'report.product.pricing': { label: 'Aplicar preço sugerido (abaixo da margem)', s: true },
    'report.stock': { label: 'Relatório de estoque' },
    'report.customer': { label: 'Relatório de clientes' },
    'report.serviceorder': { label: 'Relatório de ordens de serviço' },
    'report.vehicle': { label: 'Relatório de veículos' },
    'report.checklist': { label: 'Relatório de checklists' },
    'report.inventory': { label: 'Relatório de inventário' },
    'report.cashflow': { label: 'Fluxo de caixa' },
    'dashboard.sale': { label: 'Dashboard de vendas' },
    'dashboard.serviceorder': { label: 'Dashboard de ordens' },
    'dashboard.servicequote': { label: 'Dashboard de orçamentos' },
    'dashboard.vehicle': { label: 'Dashboard de veículos' },
    'dashboard.customer': { label: 'Dashboard de clientes' },
    'dashboard.product': { label: 'Dashboard de produtos' },
    'user.view': { label: 'Usuários: listar' },
    'user.create': { label: 'Usuários: incluir' },
    'user.update': { label: 'Usuários: editar' },
    'user.delete': { label: 'Usuários: remover', s: true },
    'profile.view': { label: 'Perfis: listar' },
    'profile.create': { label: 'Perfis: incluir' },
    'profile.update': { label: 'Perfis: editar' },
    'profile.delete': { label: 'Perfis: remover', s: true },
    'settings.serviceorder.view': { label: 'Visualizar' },
    'settings.serviceorder.update': { label: 'Alterar' },
    'settings.sale.view': { label: 'Visualizar' },
    'settings.sale.update': { label: 'Alterar' },
    'settings.general.view': { label: 'Visualizar' },
    'settings.general.update': { label: 'Alterar (inclui dados da empresa)' },
    'settings.integration.view': { label: 'Visualizar' },
    'settings.integration.update': { label: 'Alterar (conectar e desconectar)', s: true },
    'notification.view': { label: 'Ver notificações' },
    'notification.delete': { label: 'Apagar notificações', s: true },
    'loyalty.config': { label: 'Programa de fidelidade' },
    'audit.view': { label: 'Consultar auditoria' },
    'ai.use': { label: 'Usar o assistente' },
};

// Dependências simples: marcar uma ação marca também a "listar" do mesmo recurso;
// desmarcar a "listar" desmarca as ações que dependem dela. Só age no clique do usuário.
const DEPENDENCIES = [
    ['product.view', ['product.create', 'product.update', 'product.restock', 'product.import', 'product.export', 'product.delete']],
    ['purchase.view', ['purchase.create', 'purchase.cancel']],
    ['category.view', ['category.create', 'category.update', 'category.delete']],
    ['inventory.view', ['inventory.create', 'inventory.update', 'inventory.approve']],
    ['service.view', ['service.create', 'service.update', 'service.import', 'service.export', 'service.delete']],
    ['serviceorder.view', ['serviceorder.create', 'serviceorder.update', 'serviceorder.execute', 'serviceorder.checklist.fill', 'serviceorder.bill', 'serviceorder.send', 'serviceorder.upcoming', 'serviceorder.cancel', 'serviceorder.delete']],
    ['servicequote.view', ['servicequote.create', 'servicequote.update', 'servicequote.send', 'servicequote.delete']],
    ['customer.view', ['customer.details', 'customer.create', 'customer.update', 'customer.import', 'customer.export', 'customer.delete']],
    ['supplier.view', ['supplier.create', 'supplier.update', 'supplier.delete']],
    ['vehicle.view', ['vehicle.create', 'vehicle.update', 'vehicle.export', 'vehicle.delete']],
    ['checklist.view', ['checklist.create', 'checklist.update', 'checklist.delete']],
    ['checklist.group.view', ['checklist.group.create', 'checklist.group.update', 'checklist.group.delete']],
    ['cashregister.view', ['cashregister.history', 'cashregister.create', 'cashregister.update', 'cashregister.delete']],
    ['cardmachine.view', ['cardmachine.create', 'cardmachine.update', 'cardmachine.delete']],
    ['accounts.payable.view', ['accounts.payable.create', 'accounts.payable.update']],
    ['accounts.receivable.view', ['accounts.receivable.create', 'accounts.receivable.update']],
    ['user.view', ['user.create', 'user.update', 'user.delete']],
    ['profile.view', ['profile.create', 'profile.update', 'profile.delete']],
    ['settings.serviceorder.view', ['settings.serviceorder.update']],
    ['settings.sale.view', ['settings.sale.update']],
    ['settings.general.view', ['settings.general.update']],
    ['settings.integration.view', ['settings.integration.update']],
];

// Marcar a chave da esquerda marca automaticamente as da direita (além da dependência acima).
export const AUTO_GRANTS = {
    'cashregister.view': ['cashregister.history'],
};

// chave -> chave de "listar" exigida
export const REQUIRES = {};
// chave de "listar" -> chaves que dependem dela
export const DEPENDENTS = {};
DEPENDENCIES.forEach(([view, list]) => {
    DEPENDENTS[view] = list;
    list.forEach((k) => { REQUIRES[k] = view; });
});
